/**
 * /inventory/variance — the variance report.
 *
 * The grid itself (view filter, search, sort, mobile-card stacking, row
 * actions) is `InventoryVariance` from `@ampersandbrands/ui`, shared with
 * Cockpit. This file is the fetch, the store scoping, the PO picker, and the
 * notes modal.
 *
 * The notes modal used to always show blank/hardcoded state: the old adapter
 * set every row's `notes` to `null` because the variance endpoint does not
 * carry a per-row note, and the real note lives in the same
 * `store_item_note` audit-trail table Cockpit's item notes use
 * (`GET/POST /inventory/items/:item_no/notes`). This page now fetches that
 * endpoint when the modal opens, so "Edit note" shows the item's actual
 * latest note instead of always starting blank.
 */

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  InventoryVariance as SharedInventoryVariance,
  PoPickerSheet,
  type InvPoSummary,
  type InvVarianceRow,
  type InvVarianceView,
} from '@ampersandbrands/ui';
import { NotesModal } from '@/components/NotesModal';
import {
  useVariance,
  useCreateCountSession,
  useStorePos,
  useItemNotes,
  useAddItemNote,
} from '@/lib/useInventory';
import { isNotAvailable } from '@/lib/useStores';
import { useHqStore } from '@/lib/useHqStore';

export function InventoryVariance() {
  const [params, setParams] = useSearchParams();
  const { storeNo: hqStoreNo } = useHqStore();
  const navigate = useNavigate();

  const storeNo = params.get('store_no') ? Number(params.get('store_no')) : (hqStoreNo ?? undefined);
  const view: InvVarianceView = (['all', 'needs_count', 'variances'] as const).includes(
    params.get('filter') as InvVarianceView,
  )
    ? (params.get('filter') as InvVarianceView)
    : 'variances';

  const [notesTarget, setNotesTarget] = useState<InvVarianceRow | null>(null);
  const [pickingPo, setPickingPo] = useState(false);

  const { data, isLoading, error } = useVariance({ store_no: storeNo, filter: view });
  const createCount = useCreateCountSession();
  const posQ = useStorePos(storeNo, pickingPo);
  const notesQ = useItemNotes(notesTarget?.item_no ?? null, storeNo);
  const addNote = useAddItemNote();

  function setView(v: InvVarianceView) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('filter', v);
      return next;
    });
  }

  function startShelfCount() {
    createCount.mutate(
      { kind: 'shelf', ...(storeNo !== undefined ? { store_no: storeNo } : {}) },
      { onSuccess: ({ session }) => navigate(`/inventory/${session.id}${storeNo !== undefined ? `?store_no=${storeNo}` : ''}`) },
    );
  }

  function handlePoSelect(po: InvPoSummary) {
    createCount.mutate(
      { kind: 'receiving', po_no: po.po_no, ...(storeNo !== undefined ? { store_no: storeNo } : {}) },
      { onSuccess: ({ session }) => navigate(`/inventory/${session.id}${storeNo !== undefined ? `?store_no=${storeNo}` : ''}`) },
    );
  }

  const rows = data?.rows ?? [];
  const notAvailable = isNotAvailable(error);
  const latestNote = notesQ.data?.notes?.[0]?.note ?? '';

  return (
    <div className="flex min-h-0 flex-col gap-4 pb-6">
      <div className="shrink-0">
        <Link
          to="/inventory"
          className="mb-1 inline-flex items-center gap-1 text-sm text-candy-ink-muted hover:text-candy-ink dark:text-candy-dark-ink-muted dark:hover:text-candy-dark-ink"
        >
          <ArrowLeft className="h-3 w-3" /> Inventory counts
        </Link>
        <h1 className="text-xl font-semibold text-candy-ink dark:text-candy-dark-ink">Inventory Variance</h1>
      </div>

      {notAvailable ? (
        <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
          Variance report is not available yet.
        </p>
      ) : (
        <SharedInventoryVariance
          rows={rows}
          totals={data?.totals}
          loading={isLoading}
          storeSelected={storeNo !== undefined}
          view={view}
          onViewChange={setView}
          onStartShelf={startShelfCount}
          onReceiveOrder={() => setPickingPo(true)}
          actionsPending={createCount.isPending}
          onOpenNotes={(r) => setNotesTarget(r)}
        />
      )}

      <NotesModal
        isOpen={notesTarget !== null}
        initialNotes={latestNote}
        title={notesTarget ? `Notes — ${notesTarget.item_no}` : 'Notes'}
        isSaving={addNote.isPending}
        onSave={(note) => {
          if (!notesTarget) return;
          addNote.mutate(
            { item_no: notesTarget.item_no, note, store_no: storeNo },
            { onSuccess: () => setNotesTarget(null) },
          );
        }}
        onClose={() => setNotesTarget(null)}
      />

      <PoPickerSheet
        open={pickingPo}
        pos={posQ.data?.pos ?? []}
        loading={posQ.isLoading}
        emptyMessage="No open purchase orders found."
        onPick={(po) => {
          setPickingPo(false);
          handlePoSelect(po);
        }}
        onClose={() => setPickingPo(false)}
      />
    </div>
  );
}
